import axios from 'axios';
import {
  ADMIN_VERIFY_APPLICATION_URL,
  CREATE_ENTITY_APPLICATION_ONLY_URL,
  DASHBOARD_WORKFLOW_STATUS_COUNTS_URL,
  CREATE_JOURNAL_URL,
  GET_APPLICATION_DOCUMENTS_URL,
  GET_APPLICATION_FORMS_URL,
  GET_JOURNAL_URL,
  GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
  GET_FORM_DETAILS_URL,
  GET_FORMS_URL,
  LIST_ENTITY_PROFILES_URL,
  SUPERSET_DASHBOARD_GUEST_TOKEN_URL,
  SUBMIT_APPLICATION_FORM_ANSWERS_URL,
  UPDATE_JOURNAL_URL,
  UPLOAD_ADDITIONAL_DOCUMENTS_URL,
  VIEW_ENTITY_TYPE_URL,
  get_auth_headers,
  get_request_config,
} from '@/utils/system_constants/constant.js';

function parse_json_string(value, fallback = {}) {
  try { return JSON.parse(value); }
  catch { return fallback; }
}

function parse_response_data(response) {
  const raw_data = response?.data ?? response ?? {};

  if (typeof raw_data !== 'string') {
    return raw_data;
  }

  const parsed_data = parse_json_string(raw_data);
  return typeof parsed_data === 'string' ? parse_json_string(parsed_data) : parsed_data;
}

function to_array(value) {
  return Array.isArray(value) ? value : [];
}

function is_active_form(form = {}) {
  return form?.is_active === true;
}

function to_comma_separated_ids(values = []) {
  const normalized_ids = [...new Set(
    to_array(values)
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)
  )];

  return normalized_ids.join(',');
}

export const fetch_land_court_entity_types = async (params = {}) => {
  const response = await axios.get(
    VIEW_ENTITY_TYPE_URL,
    get_request_config({ params })
  );

  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const fetch_land_court_config = async (entity_type_id) => {
  const response = await axios.get(
    GET_FULL_CONFIG_BY_ENTITY_TYPE_URL,
    get_request_config({
      params: { entity_type_id }
    })
  );

  return parse_response_data(response)?.data || {};
};

export const fetch_land_court_forms = async (params = {}) => {
  const response = await axios.get(
    GET_FORMS_URL,
    get_request_config({ params: { ...params, is_active: true } })
  );

  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed).filter(is_active_form);
};

export const fetch_land_court_form_details = async (form_id) => {
  const response = await axios.get(
    GET_FORM_DETAILS_URL,
    get_request_config({
      params: { form_id }
    })
  );

  return parse_response_data(response)?.data || {};
};

export const fetch_land_court_entity_profiles = async (params = {}) => {
  const normalized_params = {
    ...params,
  };

  if (Array.isArray(normalized_params.question_ids)) {
    normalized_params.question_ids = to_comma_separated_ids(normalized_params.question_ids);
  }
  if (Array.isArray(normalized_params.fields)) {
    normalized_params.fields = normalized_params.fields
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(',');
  }

  const response = await axios.get(
    LIST_ENTITY_PROFILES_URL,
    get_request_config({ params: normalized_params })
  );

  const parsed = parse_response_data(response);
  return {
    data: to_array(parsed?.data || parsed),
    field_labels: parsed?.field_labels || {},
    pagination: parsed?.pagination || {},
  };
};

export const fetch_land_court_dashboard_status_counts = async (entity_type_id, params = {}) => {
  const response = await axios.get(
    DASHBOARD_WORKFLOW_STATUS_COUNTS_URL,
    get_request_config({
      params: { ...params, entity_type_id }
    })
  );

  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const fetch_land_court_superset_dashboard = async () => {
  const response = await axios.get(
    SUPERSET_DASHBOARD_GUEST_TOKEN_URL,
    get_request_config()
  );

  const parsed = parse_response_data(response);
  return parsed?.data || parsed || {};
};

export const fetch_land_court_application_forms = async (application_id) => {
  const response = await axios.get(
    GET_APPLICATION_FORMS_URL,
    get_request_config({
      params: { application_id }
    })
  );

  const parsed = parse_response_data(response);
  const payload = parse_response_data(parsed?.data || parsed);
  return to_array(payload?.data || payload);
};

export const fetch_land_court_application_documents = async (application_id) => {
  const response = await axios.get(
    GET_APPLICATION_DOCUMENTS_URL,
    get_request_config({
      params: { application_id }
    })
  );

  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const fetch_land_court_journals = async (application_id) => {
  const response = await axios.get(
    GET_JOURNAL_URL,
    get_request_config({
      params: { application_id }
    })
  );

  const parsed = parse_response_data(response);
  return to_array(parsed?.data || parsed);
};

export const create_land_court_journal = async (payload = {}) => {
  const is_form_data = typeof FormData !== 'undefined' && payload instanceof FormData;
  const response = await axios.post(
    CREATE_JOURNAL_URL,
    payload,
    is_form_data
      ? {
        withCredentials: true,
        headers: {
          ...get_auth_headers(),
        },
      }
      : get_request_config({
        headers: {
          'Content-Type': 'application/json',
        },
      })
  );

  return parse_response_data(response);
};

export const update_land_court_journal = async (payload = {}) => {
  const is_form_data = typeof FormData !== 'undefined' && payload instanceof FormData;
  const response = await axios.post(
    UPDATE_JOURNAL_URL,
    payload,
    is_form_data
      ? {
        withCredentials: true,
        headers: {
          ...get_auth_headers(),
        },
      }
      : get_request_config({
        headers: {
          'Content-Type': 'application/json',
        },
      })
  );

  return parse_response_data(response);
};

export const upload_land_court_additional_documents = async (payload = {}) => {
  const response = await axios.post(
    UPLOAD_ADDITIONAL_DOCUMENTS_URL,
    payload,
    {
      withCredentials: true,
      headers: {
        ...get_auth_headers(),
      },
    }
  );

  return parse_response_data(response);
};

export const create_land_court_case = async (payload = {}) => {
  const response = await axios.post(
    CREATE_ENTITY_APPLICATION_ONLY_URL,
    payload,
    get_request_config()
  );

  return parse_response_data(response);
};

export const submit_land_court_form_answers = async (payload = {}) => {
  const response = await axios.post(
    SUBMIT_APPLICATION_FORM_ANSWERS_URL,
    payload,
    get_request_config()
  );

  return parse_response_data(response);
};

export const verify_land_court_application = async (payload = {}) => {
  const response = await axios.post(
    ADMIN_VERIFY_APPLICATION_URL,
    payload,
    get_request_config()
  );

  return parse_response_data(response);
};
